import { Injectable, inject } from '@angular/core'

import type { Locale } from 'date-fns'
import {
  endOfMonth,
  endOfYear,
  isValid,
  parse,
  startOfMonth,
  startOfYear,
} from 'date-fns'
import { enUS, nl } from 'date-fns/locale'

import type { TermContext } from '../../types/types'
import { InternationalizationService } from '../internationalization.service'
import type { DateRange } from './date-range.model'

type SupportedLocale = 'nl' | 'en' | 'qqx'

interface ParsedSearchTerms {
  dateRange?: DateRange
  remainingTerms: string[]
}

@Injectable({
  providedIn: 'root',
})
export class DateParserService {
  private readonly i18nService = inject(InternationalizationService)

  private readonly locales: Record<SupportedLocale, Locale> = {
    nl,
    en: enUS,
    qqx: enUS,
  }

  private getCurrentLocale(): Locale {
    const language = this.i18nService.getLanguagePreference()
    return this.locales[language] ?? this.locales.en
  }

  private isPartOfPhrase(term: TermContext): boolean {
    const prevTerm =
      term.index > 0
        ? term.originalPhrase.split(' ')[term.index - 1].toLowerCase()
        : ''
    const nextTerm =
      term.index < term.originalPhrase.split(' ').length - 1
        ? term.originalPhrase.split(' ')[term.index + 1].toLowerCase()
        : ''

    // Common words that suggest the month/year is part of a title or content
    const contentIndicators = [
      'in',
      'of',
      'the',
      'during',
      'about',
      'from',
      'until',
      'before',
      'after',
    ]

    return (
      contentIndicators.includes(prevTerm) ||
      contentIndicators.includes(nextTerm)
    )
  }

  private findYear(
    terms: string[],
    originalPhrase: string
  ): { year: number; remainingTerms: string[] } | undefined {
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i]
      if (/^\d{4}$/.test(term)) {
        const termContext: TermContext = {
          term,
          index: i,
          originalPhrase,
        }

        // Skip if the year appears to be part of content
        if (this.isPartOfPhrase(termContext)) {
          continue
        }

        const year = parseInt(term)
        const remainingTerms = terms.filter(t => t !== term)
        return { year, remainingTerms }
      }
    }
    return undefined
  }

  private findMonth(
    terms: string[],
    locale: Locale,
    originalPhrase: string
  ): { month: number; matchedTerm: string } | undefined {
    const currentYear = new Date().getFullYear()

    for (let i = 0; i < terms.length; i++) {
      const term = terms[i]
      const monthStr = `${term} ${currentYear}`
      const parsedMonth = parse(monthStr, 'MMMM yyyy', new Date(), { locale })

      if (isValid(parsedMonth)) {
        const termContext: TermContext = {
          term,
          index: i,
          originalPhrase,
        }

        // Skip if the month appears to be part of content
        if (this.isPartOfPhrase(termContext)) {
          continue
        }

        return {
          month: parsedMonth.getMonth(),
          matchedTerm: term,
        }
      }
    }
    return undefined
  }

  /**
   * Extracts a date range from an array of search terms. Supports formats:
   *
   * - Year: "2024"
   * - Month and Year: "January 2024", "2024 January", "January text 2024"
   * - Just Month: "January" (uses current year)
   *
   * Ignores months and years that appear to be part of content (e.g., "The
   * January Revolution" or "Events of 2024")
   *
   * @param searchTerms Array of individual search terms
   * @returns Object containing the extracted date range (if found) and
   *   remaining terms
   */
  extractDateRangeFromSearchTerms(searchTerms: string[]): ParsedSearchTerms {
    const locale = this.getCurrentLocale()
    const originalPhrase = searchTerms.join(' ')
    let remainingTerms = [...searchTerms]

    // Try to find a year first
    const yearResult = this.findYear(searchTerms, originalPhrase)
    if (yearResult) {
      remainingTerms = yearResult.remainingTerms

      // Try to find a month in the remaining terms
      const monthResult = this.findMonth(remainingTerms, locale, originalPhrase)

      if (monthResult) {
        // We have both month and year
        remainingTerms = remainingTerms.filter(
          term => term !== monthResult.matchedTerm
        )
        const date = new Date(yearResult.year, monthResult.month)
        return {
          dateRange: {
            start: startOfMonth(date),
            end: endOfMonth(date),
          },
          remainingTerms,
        }
      }

      // We only have a year
      return {
        dateRange: {
          start: startOfYear(new Date(yearResult.year, 0)),
          end: endOfYear(new Date(yearResult.year, 0)),
        },
        remainingTerms,
      }
    }

    // No year found, try to find just a month
    const monthResult = this.findMonth(searchTerms, locale, originalPhrase)
    if (monthResult) {
      remainingTerms = searchTerms.filter(
        term => term !== monthResult.matchedTerm
      )
      const currentYear = new Date().getFullYear()
      const date = new Date(currentYear, monthResult.month)
      return {
        dateRange: {
          start: startOfMonth(date),
          end: endOfMonth(date),
        },
        remainingTerms,
      }
    }

    return { remainingTerms }
  }
}
